import {ReactNode, useMemo} from 'react'
import {Column, ColumnInstance, Row, useTable} from 'react-table'

import {CustomRow} from './CustomRow'
import {HeaderColumnCustom} from './HeaderColumnCustom'
import LoadingBar, {LoadingBar2} from '../Loading/LoadingBar'

export interface TableContentProps<TableData extends object> {
  children?: ReactNode
  isLoading: boolean
  tableData: TableData[]
  tableColumn: ReadonlyArray<Column<TableData>>
  className?: string
  error?: null | object
  resetFilter?: () => void
  loadingbar?: boolean
  isFetching?: boolean
  bottomRows?: ReactNode | null
}

const TableContent = <TableData extends object>({
  children,
  isLoading,
  tableData,
  tableColumn,
  className,
  error = null,
  resetFilter,
  loadingbar = false,
  isFetching = false,
  bottomRows = null,
}: TableContentProps<TableData>) => {
  const data = useMemo(() => tableData, [tableData])
  const columns = useMemo(() => tableColumn, [tableColumn])
  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable({
    columns,
    data,
  })

  return (
    <div className='table-container'>
      <div className='table-responsive'>
        <table
          id='kt_table_users'
          className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer table-sm gx-4'
          {...getTableProps()}
          // style={{borderCollapse: 'separate'}}
        >
          <thead className=''>
            <tr className='text-start text-gray-900 fw-bolder fs-7 text-uppercase gs-0'>
              {headers.map((column: ColumnInstance<TableData>) => (
                <HeaderColumnCustom key={column.id} column={column} />
              ))}
            </tr>
          </thead>
          <tbody className={`text-gray-800 fw-bold ${className}`} {...getTableBodyProps()}>
            {loadingbar && (
              <tr className='p-0 loading-bar-table'>
                <td colSpan={columns.length} className='p-0 m-0'>
                  {<LoadingBar2 show={isFetching} />}
                </td>
              </tr>
            )}
            {rows.length > 0 ? (
              <>
                {rows.map((row: Row<TableData>, i) => {
                  prepareRow(row)
                  return <CustomRow row={row} key={`row-${i}-${row.id}`} />
                })}
                {bottomRows && bottomRows}
              </>
            ) : (
              <tr>
                {resetFilter ? (
                  <td colSpan={columns.length}>
                    <div className='text-center'>
                      <p className='fs-3 text-gray-800 mb-0'>
                        No messages match your search and filter criteria
                      </p>
                      <button
                        type='button'
                        className='btn btn-transparent text-uppercase text-primary'
                        onClick={resetFilter}
                      >
                        Reset Filter
                      </button>
                    </div>
                  </td>
                ) : (
                  <td colSpan={columns.length}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                      No matching records found
                    </div>
                  </td>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {children}
    </div>
  )
}

export default TableContent
